<template>
  <section>
    <div class="flex flex-row justify-between w-full">
      <div class="flex flex-col gap-4 items-start">
        <div class="flex flex-row gap-8 items-center">
          <h3>{{ planTitleText }}</h3>
          <BittsTag
            v-if="isConnectorTier && hasSubscription && !isNotPaying"
            :color="tagColor"
            size="x-small"
            variant="rounded"
          >
            • <span data-testid="account-status">{{ accountStatus }}</span>
          </BittsTag>
        </div>
        <p>
          {{ planDurationText }} <b>{{ planDurationDate }}</b>
        </p>
        <BittsLink
          v-if="showBillingAndPaymentTerms"
          text="View billing & payment terms"
          url="https://www.crossbeam.com/legal/billing-and-payment-terms"
          target="_blank"
        />
      </div>
      <BittsButton
        v-if="showPlanManagementCTA"
        :text="isSupernodeOrEnterpriseTier ? 'Contact us' : 'View billing'"
        variant="outline"
        size="small"
        class="self-start"
        @click="onManagePlan"
      />
    </div>
    <p v-if="trialEndDate && isOnFreeTrial" class="pt-16"
      >Your trial is over on <b>{{ trialEndDate }}</b></p
    >
    <p v-else-if="isConnectorTier && !hasSubscription" class="pt-16"
      >You’re currently using order forms for your plan with us</p
    >
    <div v-if="isSupernodeOrEnterpriseTier" data-testid="account-mapping-pass">
      <div class="flex items-center gap-4 mt-20">
        <FontAwesomeIcon
          :icon="['fak', 'unlock']"
          :style="{ height: '16px', width: '16px', color: 'currentColor' }"
          class="text-primary-accent"
        />
        <h3 class="text-base">Account Mapping Pass</h3>
      </div>
      <p
        >You've unlocked unlimited account mapping for your partners on the free
        plan</p
      >
      <BittsLink
        text=" Learn more about Account Mapping Pass"
        url="https://help.crossbeam.com/en/articles/10592414-account-mapping-pass-in-crossbeam-how-it-works"
        target="_blank"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { BittsButton, BittsLink, BittsTag } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import useAuth from '@/composables/useAuth';
import { ACCOUNT_STATUS_MAP, TIER_TO_READABLE_NAME } from '@/constants/billing';
import { useBillingStore } from '@/stores';
import { extractLocalDate } from '@/utils';

const {
  billingTier,
  billingTierCreatedDate,
  hasSubscription,
  isConnectorTier,
  isSupernodeTier,
  isNotPaying,
  isOnFreeTrial,
  freeTrialEndDate,
  subscriptionStatus,
} = storeToRefs(useBillingStore());

const { currentOrg } = useAuth();

const accountStatus = computed(
  () => ACCOUNT_STATUS_MAP[subscriptionStatus.value as string]?.text,
);
const tagColor = computed(
  () => ACCOUNT_STATUS_MAP[subscriptionStatus.value as string]?.color,
);

const planTitleText = computed(() => {
  if (isNotPaying.value) return "You're on the Free plan";
  return `You're on ${TIER_TO_READABLE_NAME[billingTier.value]}`;
});
const planDurationText = computed(() => {
  if (isNotPaying.value) return 'Thanks for being on Crossbeam since';
  return 'Thanks for being on this plan since';
});

function getDate(date?: string | null) {
  if (!date) return null;
  return extractLocalDate(DateTime.fromISO(date));
}
const planDurationDate = computed(() => {
  if (isNotPaying.value) return getDate(currentOrg.value.created_at);
  return billingTierCreatedDate.value;
});

const trialEndDate = computed(() => getDate(freeTrialEndDate.value));

const isSupernodeOrEnterpriseTier = computed(() => {
  return isSupernodeTier.value /* || isEnterpriseTier */;
});

const showBillingAndPaymentTerms = computed(() => {
  if (isNotPaying.value) return false;
  if (isSupernodeOrEnterpriseTier.value) return true;
  return hasSubscription.value;
});

const showPlanManagementCTA = computed(() => {
  if (isNotPaying.value) return false;
  if (isSupernodeOrEnterpriseTier.value) return true;
  return !hasSubscription.value;
});
function onManagePlan() {
  // TODO
  if (isSupernodeOrEnterpriseTier.value) console.log('Contact us');
  else console.log('View billing');
}
</script>
